import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';

// Layout
import Layout from './components/common/Layout';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';

// Dashboard
import Dashboard from './components/dashboard/Dashboard';

// Business Components
import BusinessSetup from './components/businesses/BusinessSetup';
import BusinessProfile from './components/businesses/BusinessProfile';
import WorkingHours from './components/businesses/WorkingHours';
import BusinessPortfolio from './components/businesses/BusinessPortfolio';

// Staff Components
import StaffList from './components/staff/StaffList';
import StaffSchedule from './components/staff/StaffSchedule';

// Service Components
import ServiceList from './components/services/ServiceList';

// Booking Components
import BookingsCalendar from './components/bookings/BookingsCalendar';
import CreateBooking from './components/bookings/CreateBooking';
import BookingDetails from './components/bookings/BookingDetails';
import BookingConfirmation from './pages/BookingConfirmation';

// Review Components
import MyReviews from './components/reviews/MyReviews';

// Subscription Components
import SubscriptionPlans from './components/subscriptions/SubscriptionPlans';

// Notification Components
import Preferences from './components/notifications/Preferences';

// Report Components
import AnalyticsDashboard from './components/reports/AnalyticsDashboard';

// User Components
import UserProfile from './components/users/UserProfile';
import Favorites from './components/users/Favorites';

// Settings
import Settings from './components/settings/Settings';

// Admin Components
import AdminLogin from './pages/admin/AdminLogin';
import AdminSignup from './pages/admin/AdminSignup';
import AdminDashboard from './pages/admin/AdminDashboard';

// Error Boundary
import ErrorBoundary from './components/common/ErrorBoundary';

// Smart Registration Components
import UserTypeSelection from './components/auth/UserTypeSelection';
import DynamicRegister from './components/auth/DynamicRegister';
import RegistrationPending from './components/auth/RegistrationPending';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const queryClient = new QueryClient();

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/choose-type" element={<UserTypeSelection />} />
      <Route path="/register/:type" element={<DynamicRegister />} />
      <Route path="/registration-pending" element={<RegistrationPending />} />
      
      {/* Unified Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/signup" element={<AdminSignup />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      
      {/* Legacy Redirects for Consistency */}
      <Route path="/login/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/signup/admin" element={<Navigate to="/admin/signup" replace />} />
      
      {/* Protected Main App Routes - Inside Layout */}
      <Route element={<Layout />}>
        <Route path="/" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Business Routes */}
        <Route path="/business/setup" element={
          <ProtectedRoute allowedRoles={['business_owner']}>
            <BusinessSetup />
          </ProtectedRoute>
        } />
        <Route path="/business/profile" element={
          <ProtectedRoute allowedRoles={['business_owner']}>
            <BusinessProfile />
          </ProtectedRoute>
        } />
        <Route path="/business/hours" element={
          <ProtectedRoute allowedRoles={['business_owner', 'staff']}>
            <WorkingHours />
          </ProtectedRoute>
        } />
        <Route path="/business/portfolio" element={
          <ProtectedRoute allowedRoles={['business_owner']}>
            <BusinessPortfolio />
          </ProtectedRoute>
        } />
        
        {/* Staff Routes */}
        <Route path="/staff" element={
          <ProtectedRoute allowedRoles={['business_owner', 'admin']}>
            <StaffList />
          </ProtectedRoute>
        } />
        <Route path="/staff/schedule" element={
          <ProtectedRoute allowedRoles={['business_owner', 'staff', 'admin']}>
            <StaffSchedule />
          </ProtectedRoute>
        } />
        
        {/* Service Routes */}
        <Route path="/services" element={
          <ProtectedRoute allowedRoles={['business_owner', 'staff']}>
            <ServiceList />
          </ProtectedRoute>
        } />
        
        {/* Booking Routes */}
        <Route path="/bookings" element={
          <ProtectedRoute>
            <BookingsCalendar />
          </ProtectedRoute>
        } />
        <Route path="/bookings/create" element={
          <ProtectedRoute allowedRoles={['customer', 'staff']}>
            <CreateBooking />
          </ProtectedRoute>
        } />
        <Route path="/business/:businessId/book" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CreateBooking />
          </ProtectedRoute>
        } />
        <Route path="/bookings/:id" element={
          <ProtectedRoute>
            <BookingDetails />
          </ProtectedRoute>
        } />
        <Route path="/booking-confirmation" element={
          <ProtectedRoute>
            <BookingConfirmation />
          </ProtectedRoute>
        } />
        
        {/* Review Routes */}
        <Route path="/my-reviews" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <MyReviews />
          </ProtectedRoute>
        } />
        
        {/* Subscription Routes */}
        <Route path="/subscriptions" element={
          <ProtectedRoute allowedRoles={['business_owner']}>
            <SubscriptionPlans />
          </ProtectedRoute>
        } />
        
        {/* Notification Routes */}
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Preferences />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        } />

        <Route path="/favorites" element={
          <ProtectedRoute allowedRoles={['customer']}>
            <Favorites />
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        
        {/* Analytics Routes */}
        <Route path="/analytics" element={
          <ProtectedRoute allowedRoles={['business_owner', 'admin']}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ChatProvider>
            <Router>
              <AppRoutes />
              <Toaster position="top-right" />
            </Router>
          </ChatProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
